import {
  COINS, GAME_STATUS, SIDE_HEADS, SIDE_TAILS,
} from '@/views/games/Coinflip/utils'
import { Model } from '@vuex-orm/core'

const MAX_TICKETS = 100e3
const MAX_COMPLETED_GAMES = 25
export const MARK_ROLLED_AFTER = 10e3

class CoinflipGame extends Model {
  static entity = 'coinflip-game'

  static fields() {
    return {
      id: this.string(''),
      status: this.number(0),
      players: this.attr([]),
      creatorId: this.string(''),
      fairness: this.attr({}),
      data: this.attr({}),
      rakedItems: this.attr([]),
      createdAt: this.attr(),
      expiresAt: this.attr(),
      expiresAtDifferenceMs: this.attr(),
      lockingAt: this.attr(),
      lockingAtDifferenceMs: this.attr(),
      rolledAt: this.attr(),
      rolledAtDifferenceMs: this.attr(),
      outcome: this.number(),
    }
  }

  get joinable() {
    return this.status === GAME_STATUS.open
  }

  get amount() {
    return this.players.find(i => i.steamid === this.creatorId).total
  }

  get total() {
    return this.players.reduce((acc, player) => acc + player.total, 0)
  }

  get items() {
    return this.players
      .flatMap(player => player.items
        .map(item => ({ ...item, side: player.side, owner: player.steamid })))
  }

  get coin() {
    return COINS.find(coin => coin.id === this.data.coin) ?? COINS[0]
  }

  get heads() {
    return this.players.find(i => i.side === SIDE_HEADS)
  }

  get tails() {
    return this.players.find(i => i.side === SIDE_TAILS)
  }

  get creator() {
    return this.players.find(i => i.steamid === this.creatorId)
  }

  get joinee() {
    return this.players.find(i => i.steamid !== this.creatorId)
  }

  get winner() {
    return this.players.find(i => i.steamid === this.data.winner)
  }

  get range() {
    const { amount, data: { margin } } = this

    const min = Math.floor(amount * (1 - margin))
    const max = Math.ceil(amount * (1 + margin))

    return [min, max]
  }

  get tickets() {
    const { heads, tails, total } = this

    const playersBySide = {
      [SIDE_HEADS]: heads,
      [SIDE_TAILS]: tails,
    }

    const playerTickets = []
    let ticketsTotal = 0

    // build tickets array in heads,tails order
    for (const side of [0, 1]) {
      const player = playersBySide[side]
      // eslint-disable-next-line no-continue
      if (!player) continue

      const lastPlayer = playerTickets[playerTickets.length - 1]
      let start = 0

      if (lastPlayer) {
        // increment by 1 ticket or else 2 players own same ticket
        start = lastPlayer.end + 1
      }

      // inting tickets since roll is always an int, flooring to avoid going over 100%
      const tickets = Math.floor((player.total / total) * MAX_TICKETS)
      const end = start + tickets - 1 // roll starts at 0

      ticketsTotal += tickets

      playerTickets.push({
        steamid: player.steamid,
        side,
        start,
        end,
        tickets,
        percent: tickets / MAX_TICKETS,
      })
    }

    // if there is a discrepancy, gift remainder to last player
    const remainder = MAX_TICKETS - ticketsTotal

    if (remainder > 0) {
      const lastPlayer = playerTickets[playerTickets.length - 1]
      lastPlayer.tickets += remainder
      lastPlayer.end += remainder
    }

    return playerTickets
  }

  get outcomePercent() {
    return this.outcome / MAX_TICKETS
  }

  get rolled() {
    const rolledAt = new Date(this.rolledAt).getTime()
    return rolledAt + MARK_ROLLED_AFTER < Date.now()
  }

  // start to delete completed games after a while
  static afterCreate() {
    const completedGames = CoinflipGame.query()
      .where(game => game.status > GAME_STATUS.locked)
      .orderBy('rolledAt', 'asc')
      .get()

    if (completedGames.length > MAX_COMPLETED_GAMES) {
      completedGames[0].$delete()
    }
  }
}

export default CoinflipGame
